import React, { useState, useEffect, useCallback } from 'react';
import { listIntakeJobs, reviewIntakeJob, elationChartUrl } from '../lib/intake-api';
import { IntakeLinksPanel } from '../components/intake-links-panel';

const STATUS_LABEL = {
  done: 'Done', failed: 'Failed', processing: 'Processing', queued: 'Queued', collecting: 'Collecting',
};
const FLAG_LABEL = {
  elation_write_failed: 'Write failed', created_new_on_fuzzy: 'New chart (fuzzy)', rank_conflict: 'Rank conflict',
  eligibility_error: 'Eligibility error', eligibility_inactive: 'Inactive', insurance_id_missing: 'No insurance id',
  card_rank_missing: 'Card rank missing', image_unreadable: 'Image unreadable', low_confidence_ocr: 'Low confidence',
  min_data_gate: 'Insufficient data',
  carrier_unmapped: 'Payer not matched', carrier_low_confidence: 'Payer linked — verify', carrier_autocorrected: 'Payer auto-corrected', pdf_card_skipped: 'PDF card not attached',
  carrier_not_enrolled: 'Payer not enrolled', eligibility_id_mismatch: 'Eligibility ID mismatch',
  patient_duplicate: 'Matched existing chart',
};

function eligChipClass(s) {
  return s === 'active' ? 'elig-active' : s === 'inactive' ? 'elig-inactive' : 'elig-error';
}

function IntakeRow({ job, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const name = [job.patient.lastName, job.patient.firstName].filter(Boolean).join(', ')
    || (job.matchDecision === 'created_new' ? 'New scan' : '(no name)');
  const chartUrl = elationChartUrl(job.elationPatientId);
  const when = job.createdAt ? new Date(job.createdAt).toLocaleString() : '';

  async function markReviewed() {
    setBusy(true); setError(null);
    try { await reviewIntakeJob(job.id, true); onReviewed(job.id); setBusy(false); }
    catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className={`intake-row ${job.matchDecision === 'created_new' ? 'created-new' : ''}`}>
      <div className="intake-row-head" onClick={() => setOpen((o) => !o)}>
        <div className="intake-row-main">
          <strong>{name}</strong>
          {job.patient.dob && <span className="intake-dob"> · DOB {job.patient.dob}</span>}
          <span className={`intake-badge ${job.matchDecision === 'created_new' ? 'badge-new' : 'badge-match'}`}>
            {job.matchDecision === 'created_new' ? 'Created new' : 'Matched'}
          </span>
        </div>
        <div className="intake-row-chips">
          {job.policies.map((p, i) => (
            <span key={i} className={`elig-chip ${eligChipClass(p.eligibilityStatus)}`}>
              {p.rank}: {p.eligibilityStatus || '—'}
            </span>
          ))}
          {job.riskFlags.map((f) => (
            <span key={f} className="risk-chip">{FLAG_LABEL[f] || f}</span>
          ))}
          <span className="intake-status">{STATUS_LABEL[job.status] || job.status}</span>
          <span className="intake-when tabnum">{when}</span>
        </div>
      </div>
      {open && (
        <div className="intake-row-detail">
          {job.policies.length > 0 && (
            <div className="intake-policies">
              {job.policies.map((p, i) => (
                <div key={i} className="intake-policy">
                  <strong>{p.rank}</strong> — {p.carrier || '(carrier?)'}{p.memberIdLast4 ? ` ••••${p.memberIdLast4}` : ''}
                  {' · '}<span className={eligChipClass(p.eligibilityStatus)}>{p.eligibilityStatus || '—'}</span>
                  {p.carrierConfidence && p.carrierConfidence !== 'exact' && p.carrierConfidence !== 'alias' && (
                    <span className="risk-chip"> payer: {p.carrierConfidence}</span>
                  )}
                  {p.canCheckEligibility === false && p.eligibilityStatus === 'not_checkable' && (
                    <span className="risk-chip"> eligibility not checkable — set payer</span>
                  )}
                  {p.copay != null && <> · copay ${p.copay}</>}
                  {p.deductible != null && <> · deductible ${p.deductible}</>}
                  {p.planName && <> · {p.planName}</>}
                  {Array.isArray(p.outOfPocket) && p.outOfPocket.length > 0 && (
                    <> · OOP max ${p.outOfPocket[0].amount ?? p.outOfPocket[0].value ?? '—'}</>
                  )}
                  {p.carrierConfidence && p.carrierConfidence !== 'none' && (
                    <span className="intake-readiness">
                      {p.carrierEnrolled ? ' ✓ enrolled (eligibility)' : ' ⚠ not enrolled'}
                      {p.carrierCredentialed ? ' · ✓ credentialed (claims)' : ' · ⚠ not credentialed'}
                    </span>
                  )}
                  {p.rankConflict && <span className="risk-chip"> rank conflict (inferred {p.inferredRank})</span>}
                </div>
              ))}
            </div>
          )}
          {job.matchDecision === 'created_new' && job.candidates.length > 0 && (
            <div className="intake-candidates">
              <div className="intake-candidates-label">Possible existing charts (verify no duplicate):</div>
              {job.candidates.map((c, i) => (
                <div key={i} className="intake-candidate">
                  {[c.lastName, c.firstName].filter(Boolean).join(', ') || c.elationId || JSON.stringify(c)}
                </div>
              ))}
            </div>
          )}
          {job.error && <div className="intake-error">Error: {job.error}</div>}
          <div className="intake-actions">
            {chartUrl && (
              <a className="btn ghost sm" href={chartUrl} target="_blank" rel="noopener noreferrer">Open chart in Elation</a>
            )}
            {job.reviewedAt ? (
              <span className="intake-reviewed">✓ Reviewed</span>
            ) : (
              <button className="btn sm" disabled={busy} onClick={markReviewed}>
                {busy ? 'Saving…' : 'Mark reviewed'}
              </button>
            )}
          </div>
          {error && <div className="intake-error">{error}</div>}
        </div>
      )}
    </div>
  );
}

export function IntakeReviewPage() {
  const [jobs, setJobs] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const reload = useCallback(async () => {
    try { setJobs(await listIntakeJobs(includeReviewed)); setError(null); }
    catch (e) { setError(e.message); }
  }, [includeReviewed]);

  useEffect(() => { reload(); }, [reload]);

  function onReviewed(id) {
    setJobs((prev) => {
      if (!prev) return prev;
      return includeReviewed
        ? prev.map((j) => (j.id === id ? { ...j, reviewedAt: new Date().toISOString() } : j))
        : prev.filter((j) => j.id !== id);
    });
  }

  return (
    <div className="intake-page">
      <IntakeLinksPanel />
      <div className="intake-header">
        <h2>Intake Review</h2>
        <label className="intake-toggle">
          <input type="checkbox" checked={includeReviewed} onChange={(e) => setIncludeReviewed(e.target.checked)} />
          Show reviewed
        </label>
      </div>
      {error && <div className="intake-error">Error: {error}</div>}
      {jobs === null && <div className="intake-loading">Loading…</div>}
      {jobs && jobs.length === 0 && <div className="intake-empty">No intakes yet.</div>}
      {jobs && jobs.map((j) => <IntakeRow key={j.id} job={j} onReviewed={onReviewed} />)}
    </div>
  );
}
